/**
 * Makes the demo view-only. Prospects see the pages but cannot click into them —
 * no polymer switching, no CSV exports, no navigation.
 *
 * Two exemptions:
 *   - anything inside [data-tour-ui], so the tour's own buttons still work
 *   - untrusted events, so the tour's programmatic preAction clicks still work
 *     (element.click() sets isTrusted false, real user input sets it true)
 *
 * Listeners are capture-phase on document, which runs before React's handlers at the
 * root container, so stopPropagation prevents the app from ever seeing the event.
 */

import { Chart as ChartJS } from 'chart.js';

const TOUR_UI = '[data-tour-ui]';

const POINTER_EVENTS = [
  'click',
  'mousedown',
  'mouseup',
  'dblclick',
  'contextmenu',
  'submit',
  'change',
] as const;

/** Keys the tour itself uses; everything else that could activate a control is blocked. */
const ALLOWED_KEYS = ['ArrowRight', 'ArrowLeft', 'Escape', 'Tab'];

let installed = false;
let originalChartEvents: unknown;

/**
 * Stops charts reacting to the cursor: no tooltips, and no vertical hover line, since
 * that plugin only draws while Chart.js has active tooltip elements. Set before React
 * mounts so every chart created later inherits it.
 */
function lockCharts(): void {
  originalChartEvents = ChartJS.defaults.events;
  ChartJS.defaults.events = [];
  if (ChartJS.defaults.plugins?.tooltip) {
    ChartJS.defaults.plugins.tooltip.enabled = false;
  }
}

function unlockCharts(): void {
  ChartJS.defaults.events = originalChartEvents as typeof ChartJS.defaults.events;
  if (ChartJS.defaults.plugins?.tooltip) {
    ChartJS.defaults.plugins.tooltip.enabled = true;
  }
}

function scrollableCardArea(target: HTMLElement, deltaY: number): HTMLElement | null {
  const card = target.closest(TOUR_UI);
  if (!card) return null;

  for (let el: HTMLElement | null = target; el && card.contains(el); el = el.parentElement) {
    const room = el.scrollHeight - el.clientHeight;
    if (room < 1 || !/(auto|scroll)/.test(getComputedStyle(el).overflowY)) continue;
    if (deltaY > 0 ? el.scrollTop < room - 1 : el.scrollTop > 1) return el;
  }
  return null;
}

/**
 * Lets the page keep scrolling while the cursor sits on a tour card.
 *
 * The cards are position:fixed, so they are outside the page's scroll box and the wheel
 * finds nothing to scroll. Scroll the box ourselves when that happens.
 */
function scrollPageOverTourCard(event: WheelEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target?.closest?.(TOUR_UI)) return;
  if (scrollableCardArea(target, event.deltaY)) return;

  const scroller = document.querySelector('[data-app-scroll]');
  scroller?.scrollBy({ top: event.deltaY, left: event.deltaX });
}

/** Where the finger was last frame, so a drag can be turned into a scroll distance. */
let lastTouch: { x: number; y: number } | null = null;

function rememberTouch(event: TouchEvent): void {
  const touch = event.touches[0];
  lastTouch = touch ? { x: touch.clientX, y: touch.clientY } : null;
}

/**
 * The touch version of the above. Dragging a finger across a tour card would otherwise
 * do nothing, leaving no way past the card without reaching around it.
 */
function dragPageOverTourCard(event: TouchEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target?.closest?.(TOUR_UI)) return;

  const touch = event.touches[0];
  if (!touch || !lastTouch) return;

  if (event.cancelable) event.preventDefault();

  const top = lastTouch.y - touch.clientY;
  const left = lastTouch.x - touch.clientX;
  lastTouch = { x: touch.clientX, y: touch.clientY };

  const cardArea = scrollableCardArea(target, top);
  if (cardArea) {
    cardArea.scrollTop += top;
    return;
  }

  const scroller = document.querySelector('[data-app-scroll]');
  scroller?.scrollBy({ top, left });
}

function isExempt(event: Event): boolean {
  if (!event.isTrusted) return true;
  const target = event.target as HTMLElement | null;
  return !!target?.closest?.(TOUR_UI);
}

function blockPointer(event: Event): void {
  if (isExempt(event)) return;
  event.preventDefault();
  event.stopPropagation();
}

function blockKeys(event: KeyboardEvent): void {
  if (isExempt(event)) return;
  if (ALLOWED_KEYS.includes(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
}

export function installInteractionLock(): void {
  if (installed) return;
  POINTER_EVENTS.forEach((type) => document.addEventListener(type, blockPointer, true));
  document.addEventListener('keydown', blockKeys, true);
  document.addEventListener('wheel', scrollPageOverTourCard, { capture: true, passive: true });
  document.addEventListener('touchstart', rememberTouch, { capture: true, passive: true });
  document.addEventListener('touchmove', dragPageOverTourCard, { capture: true, passive: false });
  document.body.classList.add('demo-view-only');
  lockCharts();
  installed = true;
}

export function removeInteractionLock(): void {
  if (!installed) return;
  POINTER_EVENTS.forEach((type) => document.removeEventListener(type, blockPointer, true));
  document.removeEventListener('keydown', blockKeys, true);
  document.removeEventListener('wheel', scrollPageOverTourCard, true);
  document.removeEventListener('touchstart', rememberTouch, true);
  document.removeEventListener('touchmove', dragPageOverTourCard, true);
  lastTouch = null;
  document.body.classList.remove('demo-view-only');
  unlockCharts();
  installed = false;
}
